import React, { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TablePagination,
  IconButton,
  Chip,
  TextField,
  InputAdornment,
  Stack,
  Alert,
  CircularProgress,
  Tooltip,
  Autocomplete,
  FormControlLabel,
  Switch,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Visibility as ViewIcon,
  Delete as DeleteIcon,
  Restore as RestoreIcon,
  Search as SearchIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  LocalShipping as PickingIcon,
} from "@mui/icons-material";
import { useTSQInvMovementFulfillmentList } from "../../hooks/useTSInvMovement";
import { ListRequestSchemaV2 } from "../../types/common/commonTypes";
import { InvMovFulfillment } from "../../types/invMovementTypes";
import { paginationDefaults } from "../../config/settingsDefaults";
import { ItemOption, SOOption } from "../../types/common/selectOptions";
import { useTSQItemsMin } from "../../hooks/useTSItems";
import { useTSQSalesOrderMin } from "../../hooks/useTSSalesOrder";

type AItemOption = ItemOption<{ num: string }>;
type ASalesOrderOption = SOOption<{}>;

export const PageInvMovFulfillments: React.FC = () => {
  const navigate = useNavigate();

  const [pagination, setPagination] = useState({
    page: 0,
    size: paginationDefaults.startingRowOption,
  });
  const [orderBy, setOrderBy] = useState<string>(
    "inventory_movements.movement_date",
  );
  const [order, setOrder] = useState<"asc" | "desc">("desc");

  const [selectedSalesOrder, setSelectedSalesOrder] =
    useState<ASalesOrderOption | null>(null);
  const [selectedItem, setSelectedItem] = useState<AItemOption | null>(null);

  const [error, setError] = useState<string | null>(null);

  const [foundMovs, setFoundMovs] = useState<InvMovFulfillment[]>([]);
  const [recTotal, setRecTotal] = useState<number>(0);

  const searchFilter: string | null = useMemo(() => {
    if (!!selectedSalesOrder || !!selectedItem) {
      if (!!selectedSalesOrder) {
        if (!!selectedItem) {
          return `(sales_orders.id = '${selectedSalesOrder.value.id}' AND items.id = '${selectedItem.value.id}')`;
        } else {
          return `sales_orders.id = '${selectedSalesOrder.value.id}'`;
        }
      } else {
        return `items.id = '${selectedItem?.value.id}'`;
      }
    } else {
      return null;
    }
  }, [selectedSalesOrder, selectedItem]);

  const {
    data: movResponse,
    isLoading,
    error: queryError,
  } = useTSQInvMovementFulfillmentList(
    {
      page: pagination.page,
      page_size: pagination.size,
      sort_by: `${orderBy} ${order.toLocaleUpperCase()}`,
      filter: searchFilter,
    },
    "inv_mov_fulfillment_list",
    true,
  );

  useEffect(() => {
    if (movResponse?.items) {
      setFoundMovs(movResponse.items);
      setRecTotal(movResponse.total);
    }
  }, [movResponse]);

  const {
    data: itemsResponse,
    isLoading: itemsLoading,
    error: itemsError,
  } = useTSQItemsMin(true);
  const itemOpts: AItemOption[] = useMemo(() => {
    if (itemsResponse) {
      return itemsResponse
        .filter((i) => i.catalog !== null)
        .map((i) => ({
          label: i.catalog || i.number,
          type: "item",
          value: {
            num: i.number,
            id: i.id,
          },
        }));
    } else {
      return [];
    }
  }, [itemsResponse]);

  const {
    data: sosResponse,
    isLoading: sosLoading,
    error: soError,
  } = useTSQSalesOrderMin(true);
  const soOpts: ASalesOrderOption[] = useMemo(() => {
    if (sosResponse) {
      return sosResponse.map((so) => ({
        label: so.number,
        type: "salesorder",
        value: { id: so.id },
      }));
    } else {
      return [];
    }
  }, [sosResponse]);

  const handlePageChange = (event: unknown, newPage: number) => {
    setPagination((prev) => ({ ...prev, page: newPage }));
  };

  const handleRowsPerPageChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setPagination((prev) => ({
      ...prev,
      size: parseInt(event.target.value, 10),
      page: 0,
    }));
  };

  const handleSort = (property: string) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  return (
    <Box>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Typography variant="h4" component="h1">
          Sales Order Fulfillments
        </Typography>
      </Stack>

      {(error || queryError) && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error || queryError?.message || "An unknown error occurred"}
        </Alert>
      )}

      <Paper sx={{ mb: 2, p: 2 }}>
        <Stack direction={"column"} gap={1}>
          <Box display={"flex"} alignItems={"center"} gap={1}>
            <Typography variant="body1">Sales Order:</Typography>
            <Autocomplete
              value={selectedSalesOrder}
              options={soOpts}
              onChange={(e, v) => setSelectedSalesOrder(v)}
              sx={{ width: "30%" }}
              getOptionLabel={(opt) => opt?.label || ""}
              renderInput={(params) => <TextField {...params} />}
            />
          </Box>
          <Box display={"flex"} alignItems={"center"} gap={1}>
            <Typography variant="body1">Item:</Typography>
            <Autocomplete
              value={selectedItem}
              options={itemOpts}
              onChange={(e, v) => setSelectedItem(v)}
              sx={{ width: "30%" }}
              getOptionLabel={(opt) => opt?.label || ""}
              renderInput={(params) => <TextField {...params} />}
            />
          </Box>
        </Stack>
      </Paper>

      {isLoading && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Loading updated data...
        </Alert>
      )}

      <TableContainer component={Paper}>
        <TablePagination
          component="div"
          count={recTotal}
          page={pagination.page}
          onPageChange={handlePageChange}
          rowsPerPage={pagination.size}
          onRowsPerPageChange={handleRowsPerPageChange}
          rowsPerPageOptions={paginationDefaults.allRowOptions}
        />
        <Table>
          <TableHead>
            <TableRow>
              <TableCell
                sortDirection={
                  orderBy === "sales_orders.number" ? order : false
                }
              >
                <TableSortLabel
                  active={orderBy === "sales_orders.number"}
                  direction={orderBy === "sales_orders.number" ? order : "asc"}
                  onClick={() => handleSort("sales_orders.number")}
                >
                  Sales Order
                </TableSortLabel>
              </TableCell>
              <TableCell
                sortDirection={orderBy === "items.number" ? order : false}
              >
                <TableSortLabel
                  active={orderBy === "items.number"}
                  direction={orderBy === "items.number" ? order : "asc"}
                  onClick={() => handleSort("items.number")}
                >
                  Item Number
                </TableSortLabel>
              </TableCell>
              <TableCell
                sortDirection={
                  orderBy === "inventory_lots.lot_number" ? order : false
                }
              >
                <TableSortLabel
                  active={orderBy === "inventory_lots.lot_number"}
                  direction={
                    orderBy === "inventory_lots.lot_number" ? order : "asc"
                  }
                  onClick={() => handleSort("inventory_lots.lot_number")}
                >
                  Lot Number
                </TableSortLabel>
              </TableCell>
              <TableCell
                sortDirection={
                  orderBy === "inventory_movements.movement_date"
                    ? order
                    : false
                }
              >
                <TableSortLabel
                  active={orderBy === "inventory_movements.movement_date"}
                  direction={
                    orderBy === "inventory_movements.movement_date"
                      ? order
                      : "asc"
                  }
                  onClick={() =>
                    handleSort("inventory_movements.movement_date")
                  }
                >
                  Move Date
                </TableSortLabel>
              </TableCell>
              <TableCell>Quantity</TableCell>
              <TableCell>UOM</TableCell>
              <TableCell
                sortDirection={orderBy === "locations.name" ? order : false}
              >
                <TableSortLabel
                  active={orderBy === "locations.name"}
                  direction={orderBy === "locations.name" ? order : "asc"}
                  onClick={() => handleSort("locations.name")}
                >
                  From BIN
                </TableSortLabel>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            ) : foundMovs.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Typography variant="body2" color="text.secondary" py={4}>
                    No inventory movements found.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              foundMovs.map((mov) => {
                return (
                  <TableRow key={mov.id} hover>
                    <TableCell>
                      <Typography
                        variant="body2"
                        sx={{ ml: 1, fontWeight: "medium" }}
                      >
                        {mov.sales_order}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        sx={{ ml: 1, fontWeight: "medium" }}
                      >
                        {mov.item}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        sx={{ ml: 1, fontWeight: "medium" }}
                      >
                        {mov.lot}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        sx={{ ml: 1, fontWeight: "medium" }}
                      >
                        {new Date(mov.date).toLocaleDateString("en-US")}
                      </Typography>
                    </TableCell>
                    <TableCell>{mov.quantity}</TableCell>
                    <TableCell>{mov.uom_unit}</TableCell>
                    <TableCell>{mov.from_bin}</TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={recTotal}
          page={pagination.page}
          onPageChange={handlePageChange}
          rowsPerPage={pagination.size}
          onRowsPerPageChange={handleRowsPerPageChange}
          rowsPerPageOptions={paginationDefaults.allRowOptions}
        />
      </TableContainer>
    </Box>
  );
};

export default PageInvMovFulfillments;
